import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import { MEMORY_KEEPER_TOUR_STEPS, DEFAULT_TOUR_STAGE } from '../../lib/shared/memory-keeper-tour-steps';
import { logEvent } from '../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../lib/shared/event-types';
import 'driver.js/dist/driver.css';
import './memory-keeper-tour.css';

const TOUR_START_DELAY = 400;
const TOUR_POPOVER_CLASS = 'memoryKeeperTourPopover';

function useMemoryKeeperTour({ isEnabled, onFinish, onClose }) {
    const hasStartedRef = useRef(false);

    useEffect(() => {
        if (!isEnabled || hasStartedRef.current) {
            return undefined;
        }

        let isCompleted = false;
        let isCleanedUp = false;

        const tour = driver({
            steps: MEMORY_KEEPER_TOUR_STEPS,
            popoverClass: TOUR_POPOVER_CLASS,
            showProgress: true,
            progressText: '{{current}} of {{total}}',
            nextBtnText: 'Next',
            prevBtnText: 'Back',
            doneBtnText: 'Got it',
            smoothScroll: true,
            disableActiveInteraction: true,
            ...DEFAULT_TOUR_STAGE,
            overlayOpacity: 0.6,
            onHighlightStarted: (element, step, opts) => {
                opts.driver.setConfig({
                    ...opts.driver.getConfig(),
                    ...DEFAULT_TOUR_STAGE,
                    ...step.data?.stage
                });

                if (step.data?.stepKey) {
                    logEvent(EVENT_TYPES.MEMORY_KEEPER_ONBOARDING_STEP_VIEWED, { step: step.data.stepKey });
                }
            },
            onDoneClick: (element, step, opts) => {
                isCompleted = true;
                opts.driver.destroy();
            },
            onDestroyStarted: (element, step, opts) => {
                opts.driver.destroy();
            },
            onDestroyed: () => {
                if (isCleanedUp) {
                    return;
                }

                if (isCompleted) {
                    onFinish?.();

                    return;
                }

                onClose?.();
            }
        });

        const startTimeout = setTimeout(() => {
            hasStartedRef.current = true;
            tour.drive();
        }, TOUR_START_DELAY);

        return () => {
            clearTimeout(startTimeout);
            isCleanedUp = true;

            if (tour.isActive()) {
                tour.destroy();
            }
        };
    }, [isEnabled]);
}

export {
    useMemoryKeeperTour
};
